import React from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Modal, ModalBody } from '@razorpay/blade/components';
import { format, startOfMonth } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { Wizard, WizardStep } from 'forms/Wizard';
import api from 'api';
import {
  WizardError,
  WizardHeader,
  WizardLoader,
  WizardWrapper,
} from 'components/WizardViews/components';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { useSearchParams } from 'hooks/useSearchParams';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import WizardPathToStepController from '../components/WizardPathToStepController';
import ReviewStep from './ReviewStep';
import UpdateStructureStep from './UpdateStructureStep';
import SalaryDetailsStep from './SalaryDetailsStep';
import {
  revisePayRouteToStepMap,
  revisePayRouteToStepMapWithMSS,
  revisePaySteps,
  ReviseSalaryFormData,
} from './constants';
import { Sidebar } from './components/Sidebar';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import { useListSalaryStructures } from 'components/SalaryStructures/hooks';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import styled from 'styled-components';
import { generateUserId } from 'utils/commons';

const StyledRevisePayStepWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  height: 100%;
  scroll-behavior: smooth;
`;

const RevisePayWizard = () => {
  const history = useHistory();

  const { setFormData, formData, setFieldValue } = useFormWithProvider<ReviseSalaryFormData>();

  const { isM3S1Enabled, isM3S3Enabled } = useIsFeatureDisabledForRearch();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;

  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();

  const payrollSetupQuery = useQuery({
    queryFn: () => api.userQueries.payrollGetSetup(),
    onSuccess: () => ctcQuery.refetch(),
  });

  const ctcQuery = useQuery({
    queryKey: ['getSalaryDetails', userId],
    queryFn: () =>
      api.userQueries.getSalaryDetails({
        userId,
      }),
    enabled: false,
    onSuccess: (data) => {
      if (!data.payrollGetSalaryDetails) return;
      const payrollDetails = data.payrollGetSalaryDetails.data;
      const payrollSetupInfo = payrollSetupQuery.data!.payrollGetSetup.data;

      setFormData({
        ...formData,
        currentAnnualCtc: payrollDetails.annualSalary,
        revisedCompensation: String(payrollDetails.annualSalary),
        currentCtcEffectiveFrom: payrollDetails.effectiveFromDate,
        payrollSetupInfo,
        effectiveFrom: startOfMonth(new Date()),
      });
    },
  });

  const {
    data: salaryStructureData,
    isFetching: isFetchingSalaryStructures,
    error: listSalaryStructureError,
  } = useListSalaryStructures({
    enabled: !ctcQuery.isFetching && (isM3S1Enabled || isM3S3Enabled),
    queryKey: [salaryStructureQueries.getSalaryStructuresList],
    filters: 'SALARY_STRUCTURE_STATUS_ACTIVE',
    onSuccess: (data) => {
      if (data && !formData.salaryStructureData) {
        setFieldValue('salaryStructureData', data);
      }
    },
  });

  const { isFetching: isFetchingSalaryStructuresForUsers } = useQuery({
    queryKey: [salaryStructureQueries.getSalaryStructuresForUsers, userId],
    queryFn: () =>
      api.salaryStructures.queries.getSalaryStructuresForUsers({
        input: {
          userIds: [generateUserId(userId)],
          forMonth: format(startOfMonth(new Date()), 'yyyy-MM'),
        },
      }),
    select: (data) => {
      return data.payrollGetSalaryStructuresForUsers.data;
    },
    onSuccess: (data) => {
      const { salaryStructureUsers } = data;
      if (salaryStructureUsers?.length) {
        const salaryStructure = salaryStructureUsers[0].salaryStructure;
        setFieldValue('salaryStructureForUserInDb', salaryStructure);
        if (!formData.salaryStructureId?.[0]) {
          setFieldValue('salaryStructureId', [salaryStructure.id]);
        }
        if (isM3S3Enabled && salaryStructure.type === 'SALARY_STRUCTURE_TYPE_USER') {
          setFieldValue('isCustomSalaryStructure', true);
        }
      } else {
        const defaultSalaryStructure = salaryStructureData?.find(
          (salaryStructure) => salaryStructure.type === 'SALARY_STRUCTURE_TYPE_DEFAULT',
        )?.id;
        setFieldValue('salaryStructureId', [defaultSalaryStructure]);
      }
    },
    enabled: !isFetchingSalaryStructures && !!userId && (isM3S1Enabled || isM3S3Enabled),
  });

  const handleCloseClick = () => {
    history.push(`/pay-details/compensation?userId=${userId}`);
  };

  const shouldShowUpdateStructureStep = isM3S3Enabled
    ? !formData.isCustomSalaryStructure
    : isM3S1Enabled;

  const routeStepMap = shouldShowUpdateStructureStep
    ? revisePayRouteToStepMapWithMSS
    : revisePayRouteToStepMap;

  if (
    ctcQuery.isFetching ||
    payrollSetupQuery.isFetching ||
    !ctcQuery.data ||
    isFetchingSalaryStructures ||
    isFetchingSalaryStructuresForUsers ||
    !formData.effectiveFrom
  ) {
    return <WizardLoader />;
  }

  const error = ctcQuery.error || payrollSetupQuery.error || listSalaryStructureError;

  const payrollDetails = ctcQuery.data.payrollGetSalaryDetails.data;
  const name = payrollDetails.name;
  const firstName = name?.split(' ')[0];

  return (
    <Modal isOpen={true} onDismiss={() => {}} size="full">
      <ModalBody padding="spacing.0">
        <WizardInfoProvider initialStep={0} totalSteps={shouldShowUpdateStructureStep ? 3 : 2}>
          <WizardPathToStepController routeStepMap={routeStepMap} />
          <WizardWrapper>
            <Box
              borderBottomColor="surface.border.gray.muted"
              borderBottomWidth="thin"
              borderBottomStyle="solid">
              <WizardHeader
                heading="Revise compensation for"
                subHeading={firstName ?? 'Employee'}
                onCloseClick={handleCloseClick}
                isProgressHidden={true}
              />
            </Box>
            <Box display="flex" flex={1} height="100%" overflow="hidden">
              <Box
                padding="spacing.7"
                maxWidth="224px"
                borderRightColor="surface.border.gray.muted"
                backgroundColor="surface.background.gray.moderate"
                borderRightWidth="thin"
                overflowY="auto"
                flexShrink={0}>
                <Sidebar />
              </Box>
              <StyledRevisePayStepWrapper>
                <WizardError onRetry={payrollSetupQuery.refetch} error={error}>
                  <Wizard>
                    {[
                      <WizardStep
                        key="step1"
                        step={revisePaySteps.step1}
                        component={SalaryDetailsStep}
                      />,
                      ...(shouldShowUpdateStructureStep
                        ? [
                            <WizardStep
                              key="step2"
                              step={revisePaySteps.step2}
                              component={UpdateStructureStep}
                            />,
                          ]
                        : []),
                      <WizardStep key="step3" step={revisePaySteps.step3} component={ReviewStep} />,
                    ]}
                  </Wizard>
                </WizardError>
              </StyledRevisePayStepWrapper>
            </Box>
          </WizardWrapper>
        </WizardInfoProvider>
      </ModalBody>
    </Modal>
  );
};

export default RevisePayWizard;
